import React from 'react';
import { Box, alpha, useTheme } from '@mui/material';
import { listingStatusLabel } from '../../utils/mlsStatus';

/**
 * Copy for `mlsListing.isActive` values other than ACTIVE. WITHDRAWN is not
 * a value the MLSStatus enum carries today, but is mapped defensively since
 * upstream feeds occasionally surface it as a raw status string.
 */
const STATUS_LABELS: Record<string, string> = {
  SOLD: 'Sold',
  PENDING: 'Under contract',
  EXPIRED: 'Expired',
  WITHDRAWN: 'Withdrawn',
};

/**
 * Only PENDING reads as "still in motion" -- sold/expired/withdrawn are
 * finished states, not something to flag urgently, so they stay neutral.
 * ACTIVE only reaches the chip when the MLS's own status qualifies it
 * ("Coming Soon - No Showings"), which is in motion too.
 */
const WARNING_STATUSES = new Set(['ACTIVE', 'PENDING']);

interface ListingStatusChipProps {
  status?: string | null;
  /** The MLS's own status string; shown verbatim in place of the bucket label. */
  mlsStatus?: string | null;
}

/**
 * A small flat-tint pill next to a listing's price for anything other than
 * plain ACTIVE inventory, carrying the MLS's own status string when the feed
 * supplies one. Without this, a buyer's "Interested Homes" list
 * shows a home that sold weeks ago or expired with no indication at all --
 * the price and the "Market Analysis" chip look exactly like a live listing.
 */
const ListingStatusChip: React.FC<ListingStatusChipProps> = ({ status, mlsStatus }) => {
  const theme = useTheme();

  const label = listingStatusLabel(status, mlsStatus, STATUS_LABELS);
  if (!label) return null;

  const isWarning = WARNING_STATUSES.has(status ?? 'ACTIVE');
  const tintColor = isWarning
    ? theme.palette.warning.main
    : theme.palette.text.secondary;
  const textColor = isWarning
    ? theme.palette.warning.dark
    : theme.palette.text.secondary;

  return (
    <Box
      data-testid="listing-status-chip"
      data-status={status ?? 'ACTIVE'}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '0.7rem',
        fontWeight: 700,
        borderRadius: '10px',
        px: 1,
        py: '3px',
        // A full MLS status runs long ("Pending - Continue to Show - 1st
        // Right of Refusal") and must not be truncated, so it wraps instead.
        whiteSpace: 'normal',
        lineHeight: 1.3,
        minWidth: 0,
        color: textColor,
        backgroundColor: alpha(tintColor, 0.1),
        border: `1px solid ${alpha(tintColor, 0.25)}`,
      }}
    >
      {label}
    </Box>
  );
};

export default ListingStatusChip;
